import { Link, useNavigate } from 'react-router-dom';
import { useEffect, useMemo, useRef, useState } from 'react';
import usePrerenderReady from '@/lib/usePrerenderReady';
import axios from 'axios';
import RegisteredArtistCard from '@/components/RegisteredArtistCard';
import SEO from '@/components/SEO';
import { faqPreviewItems } from '@/lib/faqContent';
import { artistProfilePath } from '@/lib/artistSlug';
import { collaboratorProfilePath } from '@/lib/collaboratorSlug';
import {
  isInstituteAcademyType,
  isStudioVenueType,
} from '@/lib/collaboratorOrganizationTypes';
import { useLanguage } from '@/context/LanguageContext';
import { useArtistAuth } from '@/context/ArtistAuthContext';
import { useCollaboratorAuth } from '@/context/CollaboratorAuthContext';
import { trackEvent } from '@/lib/analytics';
import usePageContent from '@/lib/usePageContent';
import { Building2, CalendarCheck, ChevronLeft, ChevronRight, Crown, Handshake, MapPin, Search, Sparkles, UserPlus, WandSparkles } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;
const SHOW_EXTENDED_HOME_SECTIONS = false;
const SHOW_MARKETING_HOME_SECTIONS = false;
const ANDROID_PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=art.kalamanch.app';

const asArray = (value) => (Array.isArray(value) ? value : []);

const uniqueArtists = (artists = []) => {
  const seen = new Set();
  return artists.filter(Boolean).filter((artist) => {
    const identity = String(artist.id || artist.slug || artist.name || '').trim().toLowerCase();
    if (!identity || seen.has(identity)) return false;
    seen.add(identity);
    return true;
  });
};

const expandHomeSearchTokens = (query = '') => {
  const clean = String(query || '').trim().toLowerCase();
  if (!clean) return [];
  const base = clean.split(/\s+/).filter(Boolean);
  const expanded = new Set([clean, ...base]);
  if (clean.includes('singer') || clean.includes('गायक')) ['vocalist', 'music', 'singing'].forEach((item) => expanded.add(item));
  if (clean.includes('dancer') || clean.includes('नर्तक') || clean.includes('dance')) ['choreographer', 'performance'].forEach((item) => expanded.add(item));
  if (clean.includes('poet') || clean.includes('shayari') || clean.includes('शायर') || clean.includes('कवि')) ['poetry', 'writer', 'spoken word'].forEach((item) => expanded.add(item));
  if (clean.includes('painter') || clean.includes('artist') || clean.includes('चित्रकार')) ['painting', 'visual art', 'illustrator'].forEach((item) => expanded.add(item));
  if (clean.includes('actor') || clean.includes('acting') || clean.includes('अभिनेता')) ['performer', 'theatre'].forEach((item) => expanded.add(item));
  return Array.from(expanded);
};

const getYoutubeId = (url = '') => {
  const patterns = [
    /(?:youtube\.com\/watch\?.*v=|youtu\.be\/)([A-Za-z0-9_-]{6,})/,
    /youtube\.com\/(?:embed|v|shorts|live)\/([A-Za-z0-9_-]{6,})/,
  ];
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match?.[1]) return match[1];
  }
  return '';
};

const videoFromApi = (video) => ({
  id: video.youtube_id || getYoutubeId(video.video_url || ''),
  title: video.video_title || `${video.artistName || video.artist_name || 'Artist'} Performance`,
  artistName: video.artistName || video.artist_name,
  category: video.category,
  createdAt: video.created_at || '',
});

const collaboratorName = (collaborator = {}) =>
  collaborator.organization_name || collaborator.name || 'KalaManch Collaborator';

const collaboratorLookingFor = (collaborator = {}, limit = 4) => {
  if (Array.isArray(collaborator.looking_for) && collaborator.looking_for.length) {
    return collaborator.looking_for.map((item) => String(item).trim()).filter(Boolean).slice(0, limit);
  }
  return [];
};

const cleanInlineMarkdown = (value = '') => String(value || '')
  .replace(/\*{1,3}([^*]+)\*{1,3}/g, '$1')
  .replace(/_{1,3}([^_]+)_{1,3}/g, '$1')
  .trim();

const CollaboratorHomeCard = ({ collaborator }) => {
  const name = collaboratorName(collaborator);
  const publicProfession = String(collaborator.display_profession || '').trim() || collaborator.organization_category || collaborator.organization_type || 'Collaborator';
  const lookingFor = collaboratorLookingFor(collaborator, 3);
  const location = collaborator.city || collaborator.state || 'India';
  const profileImage = collaborator.image_url || collaborator.profile_image_url;

  return (
    <Link
      to={collaboratorProfilePath(collaborator)}
      onClick={() => trackEvent('home_collaborator_profile_click', { collaborator: name })}
      className="group flex h-[304px] w-[280px] shrink-0 flex-col overflow-hidden rounded-[1.75rem] border border-orange-100 bg-white p-5 text-left shadow-lg transition duration-300 hover:-translate-y-1 hover:shadow-xl sm:w-[320px]"
    >
      <div className="flex items-start gap-4">
        <span className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-2xl border border-orange-100 bg-orange-50 text-2xl font-black text-[#FF6F00]">
          {profileImage ? (
            <img src={profileImage} alt={`${name} collaborator`} className="h-full w-full object-contain bg-white p-1" loading="lazy" />
          ) : (
            name.charAt(0).toUpperCase()
          )}
        </span>
        <span className="min-w-0">
          <span className="block line-clamp-2 text-xl font-black leading-tight text-gray-950">{name}</span>
          <span className="mt-1 flex items-center gap-1 text-sm font-semibold text-[#008C7A]">
            <Building2 className="h-4 w-4 shrink-0" />
            <span className="truncate">{publicProfession}</span>
          </span>
        </span>
      </div>

      <p className="mt-4 line-clamp-3 text-sm leading-6 text-gray-600">
        {cleanInlineMarkdown(collaborator.bio) || `${name} is open to working with KalaManch artists and creative teams.`}
      </p>

      <div className="mt-4 flex items-center gap-1 text-sm font-semibold text-gray-500">
        <MapPin className="h-4 w-4 shrink-0 text-[#FF6F00]" />
        <span className="truncate">{location}</span>
      </div>

      <div className="mt-auto pt-4">
        <p className="text-[11px] font-black uppercase tracking-[0.22em] text-[#4A148C]">Looking for</p>
        {lookingFor.length > 0 ? (
          <div className="mt-2 flex flex-wrap gap-2">
            {lookingFor.map((item) => (
              <span key={item} className="rounded-full bg-teal-50 px-3 py-1 text-xs font-bold text-[#006B5C]">
                {item}
              </span>
            ))}
          </div>
        ) : (
          <p className="mt-2 rounded-2xl bg-gray-50 px-3 py-2 text-xs font-semibold text-gray-500">
            Creative talent and artist collaborations
          </p>
        )}
      </div>
    </Link>
  );
};

const Home = () => {
  const navigate = useNavigate();
  const { isHindi: _isHindi } = useLanguage();
  const isHindi = false;
  const { isAuthenticated: artistAuthenticated } = useArtistAuth();
  const { isAuthenticated: collaboratorAuthenticated } = useCollaboratorAuth();
  const [featuredArtists, setFeaturedArtists] = useState([]);
  const [spotlightOverride, setSpotlightOverride] = useState(null);
  const [performanceVideos, setPerformanceVideos] = useState([]);
  const [featuredCollaborators, setFeaturedCollaborators] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [successStories, setSuccessStories] = useState([]);
  const [liveCompetition, setLiveCompetition] = useState(null);
  const [searchableArtists, setSearchableArtists] = useState([]);
  const [homeSearchQuery, setHomeSearchQuery] = useState('');
  const [loadingHomeSearch, setLoadingHomeSearch] = useState(true);
  const [loadingArtists, setLoadingArtists] = useState(true);
  const [loadingSections, setLoadingSections] = useState({ sponsors: true, stories: true, collaborators: true, competition: true });
  usePrerenderReady(loadingArtists || loadingHomeSearch || Object.values(loadingSections).some(Boolean));
  const collaboratorCarouselRef = useRef(null);
  const performanceCarouselRef = useRef(null);
  const pageContent = usePageContent('home');
  const featuredRoster = uniqueArtists([
    spotlightOverride,
    ...featuredArtists.filter((artist) => artist.is_spotlight),
    ...featuredArtists,
  ]);
  const spotlightArtists = featuredRoster.slice(0, 3);
  const standardFeaturedArtists = featuredRoster.slice(3).filter((artist) => artist.is_featured).slice(0, 12);
  const featuredInstituteCollaborators = featuredCollaborators.filter((item) => isInstituteAcademyType(item.organization_type)).slice(0, 3);
  const featuredStudioCollaborators = featuredCollaborators.filter((item) => isStudioVenueType(item.organization_type)).slice(0, 3);
  const disciplineChips = [
    { label: 'Music', query: 'music singer musician vocalist composer' },
    { label: 'Dance', query: 'dance dancer choreographer classical folk' },
    { label: 'Theatre', query: 'theatre theater actor drama stage' },
    { label: 'Visual Art', query: 'visual art artist illustrator sculptor' },
    { label: 'Photography', query: 'photography photographer photo visual' },
    { label: 'Voice-over', query: 'voice over voiceover anchor narrator dubbing' },
    { label: 'Painting', query: 'painting painter canvas rangoli visual art' },
    { label: 'Poetry', query: 'poetry poet shayar shayari spoken word' },
    { label: 'Bands', query: 'band music group live band guitarist drummer' },
    { label: 'Folk Dance', query: 'folk dance folk dancer traditional cultural' },
  ];

  const homeArtistMatches = useMemo(() => {
    const trimmed = homeSearchQuery.trim();
    if (!trimmed) return [];
    const tokens = expandHomeSearchTokens(trimmed);
    return searchableArtists
      .filter((artist) => {
        const haystack = [
          artist.name,
          artist.category,
          artist.subcategory,
          artist.city,
          artist.state,
          artist.bio,
          artist.native_bio,
          artist.smart_fit,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return tokens.every((token) => haystack.includes(token));
      })
      .sort((a, b) => {
        const aBoost = Number(Boolean(a.is_spotlight)) * 3 + Number(Boolean(a.is_featured)) * 2 + Number(Boolean(a.is_verified));
        const bBoost = Number(Boolean(b.is_spotlight)) * 3 + Number(Boolean(b.is_featured)) * 2 + Number(Boolean(b.is_verified));
        if (bBoost !== aBoost) return bBoost - aBoost;
        return String(a.name || '').localeCompare(String(b.name || ''));
      })
      .slice(0, 6);
  }, [homeSearchQuery, searchableArtists]);

  useEffect(() => {
    const fetchSearchableArtists = async () => {
      try {
        const response = await axios.get(`${API}/artists/all`);
        setSearchableArtists(asArray(response.data.artists));
      } catch (error) {
        console.error('Failed to load homepage artist search:', error);
        setSearchableArtists([]);
      } finally {
        setLoadingHomeSearch(false);
      }
    };

    fetchSearchableArtists();
  }, []);

  useEffect(() => {
    const fetchFeaturedArtistsLegacy = async () => {
      const response = await axios.get(`${API}/artists/all`);
      const artists = asArray(response.data.artists);
      const featuredOnly = artists
        .filter((artist) => artist.is_featured && artist.is_verified)
        .slice(0, 15);
      const artistVideoGroups = await Promise.all(
        featuredOnly.slice(0, 6).map(async (artist) => {
          try {
            const videosResponse = await axios.get(`${API}/videos/artist/${artist.id}`);
            const submittedVideos = asArray(videosResponse.data.videos);

            if (submittedVideos.length > 0) {
              return submittedVideos.map((video) => ({
                id: video.youtube_id || getYoutubeId(video.video_url || ''),
                title: video.video_title || `${artist.name} Performance`,
                artistName: artist.name,
                category: artist.subcategory || artist.category,
                createdAt: video.created_at || artist.created_at || '',
              }));
            }

            return [];
          } catch (error) {
            return [];
          }
        })
      );

      return {
        artists: featuredOnly,
        spotlightArtist: null,
        videos: artistVideoGroups
          .flat()
          .filter((video) => video.id)
          .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)),
      };
    };

    const fetchFeaturedArtists = async () => {
      try {
        let data;
        try {
          const response = await axios.get(`${API}/home/featured`);
          const homeArtists = asArray(response.data.artists);
          const homeVideos = asArray(response.data.videos);
          data = {
            artists: homeArtists,
            spotlightArtist: response.data.spotlight_artist || null,
            videos: homeVideos
              .map(videoFromApi)
              .filter((video) => video.id)
              .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)),
          };
        } catch (error) {
          data = await fetchFeaturedArtistsLegacy();
        }

        setFeaturedArtists(data.artists);
        setSpotlightOverride(data.spotlightArtist || null);
        setPerformanceVideos(data.videos);
      } catch (error) {
        console.error('Failed to load featured artists:', error);
        setFeaturedArtists([]);
        setSpotlightOverride(null);
        setPerformanceVideos([]);
      } finally {
        setLoadingArtists(false);
      }
    };

    fetchFeaturedArtists();
  }, []);

  useEffect(() => {
    const fetchSponsors = async () => {
      try {
        const response = await axios.get(`${API}/sponsors/active`);
        setSponsors(asArray(response.data.sponsors));
      } catch (error) {
        setSponsors([]);
      } finally {
        setLoadingSections(current => ({ ...current, sponsors: false }));
      }
    };

    fetchSponsors();
  }, []);

  useEffect(() => {
    const fetchSuccessStories = async () => {
      try {
        const response = await axios.get(`${API}/success-stories/active`);
        setSuccessStories(asArray(response.data.stories));
      } catch (error) {
        setSuccessStories([]);
      } finally {
        setLoadingSections(current => ({ ...current, stories: false }));
      }
    };

    fetchSuccessStories();
  }, []);

  useEffect(() => {
    let mounted = true;
    const fetchCollaborators = async () => {
      try {
        const response = await axios.get(`${API}/collaborators/all`);
        const collaborators = asArray(response.data.collaborators)
          .filter((collaborator) => collaborator?.id || collaborator?.slug)
          .sort((a, b) => Number(Boolean(b.is_featured)) - Number(Boolean(a.is_featured)))
          .slice(0, 12);
        if (mounted) setFeaturedCollaborators(collaborators);
      } catch (error) {
        if (mounted) setFeaturedCollaborators([]);
      } finally {
        if (mounted) setLoadingSections(current => ({ ...current, collaborators: false }));
      }
    };
    fetchCollaborators();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    const fetchLiveCompetition = async () => {
      try {
        const response = await axios.get(`${API}/competitions`);
        const live = asArray(response.data.competitions).find((competition) => competition.status === 'live');
        setLiveCompetition(live || null);
      } catch (error) {
        setLiveCompetition(null);
      } finally {
        setLoadingSections(current => ({ ...current, competition: false }));
      }
    };

    fetchLiveCompetition();
  }, []);

  const youtubeVideos = performanceVideos;

  const openArtistJoinOrAccount = () => {
    if (artistAuthenticated) {
      navigate('/my-account');
      return;
    }
    if (collaboratorAuthenticated) {
      navigate('/collaborator/account');
      return;
    }
    window.dispatchEvent(new Event('openArtistJoin'));
  };

  const openCollaboratorJoinOrAccount = () => {
    if (collaboratorAuthenticated) {
      navigate('/collaborator/account');
      return;
    }
    if (artistAuthenticated) {
      navigate('/my-account');
      return;
    }
    window.dispatchEvent(new Event('openCollaboratorJoin'));
  };

  const scrollCollaborators = (direction) => {
    const carousel = collaboratorCarouselRef.current;
    if (!carousel) return;
    const firstCard = carousel.querySelector('[data-collaborator-carousel-card]');
    const cardWidth = firstCard?.getBoundingClientRect().width || Math.min(carousel.clientWidth * 0.85, 340);
    carousel.scrollBy({ left: direction * (cardWidth + 20), behavior: 'smooth' });
  };

  const scrollPerformances = (direction) => {
    const carousel = performanceCarouselRef.current;
    if (!carousel) return;
    const firstCard = carousel.querySelector('[data-testid^="video-card-"]');
    const cardWidth = firstCard?.getBoundingClientRect().width || Math.min(carousel.clientWidth * 0.8, 304);
    carousel.scrollBy({ left: direction * (cardWidth + 16), behavior: 'smooth' });
  };

  const platformActions = [
    {
      label: 'Join Artist',
      icon: UserPlus,
      description: 'Create your artist profile and start getting discovered on KalaManch.',
      onClick: () => {
        trackEvent('home_quick_join_artist_click');
        openArtistJoinOrAccount();
      },
    },
    {
      label: 'Collaborate',
      icon: Handshake,
      description: 'For brands, organisers, studios, venues, institutes, and teams looking for artists.',
      onClick: () => {
        trackEvent('home_quick_join_collaborator_click');
        openCollaboratorJoinOrAccount();
      },
    },
    {
      label: 'AI Studio',
      icon: Sparkles,
      to: '/ai-studio',
      event: 'home_quick_ai_studio_click',
      description: 'Generate bios, captions, hashtags, scripts, and creator content with KalaManch AI.',
    },
    {
      label: 'Event Planner',
      icon: CalendarCheck,
      to: '/event-planner',
      event: 'home_quick_event_planner_click',
      description: 'Plan events, explore artist types, and build stronger creative lineups.',
    },
    {
      label: 'Casting Director',
      icon: WandSparkles,
      to: '/casting/post',
      event: 'home_quick_casting_director_click',
      description: 'Post a brief and let KalaManch suggest artist matches for your requirement.',
    },
    {
      label: 'AI Mentor Pro',
      icon: Crown,
      to: '/ai-mentor',
      event: 'home_quick_ai_mentor_click',
      description: 'Paid mentor for serious artists: critique, practice drills, pricing, market insights, and growth roadmap.',
    },
  ];

  const liveActivityItems = [
    ...featuredArtists.slice(0, 2).map((artist) => ({
      label: isHindi ? 'Featured Artist' : 'Featured Artist',
      title: artist.name,
      text: isHindi
        ? `${artist.subcategory || artist.category || 'Artist'} profile admin verified है।`
        : `${artist.subcategory || artist.category || 'Artist'} profile is admin verified.`,
      accent: 'from-[#FF6F00] to-[#C2185B]',
    })),
    ...performanceVideos.slice(0, 2).map((video) => ({
      label: isHindi ? 'New Performance' : 'New Performance',
      title: video.artistName || 'Artist performance',
      text: isHindi
        ? 'Approved YouTube performance showcase में जोड़ा गया।'
        : 'Approved YouTube performance added to the showcase.',
      accent: 'from-[#4A148C] to-[#6A1B9A]',
    })),
    ...sponsors.slice(0, 1).map((sponsor) => ({
      label: isHindi ? 'Brand Partner' : 'Brand Partner',
      title: sponsor.brand_name,
      text: isHindi
        ? 'Creative talent ecosystem को support करने वाला partner.'
        : 'Partner supporting the creative talent ecosystem.',
      accent: 'from-[#00BFA5] to-[#0F766E]',
    })),
  ].slice(0, 4);

  const fallbackActivityItems = [
    {
      label: isHindi ? 'Admin Review' : 'Admin Review',
      title: isHindi ? 'Verified profiles' : 'Verified profiles',
      text: isHindi ? 'Public artist visibility admin review के बाद होती है।' : 'Public artist visibility starts after admin review.',
      accent: 'from-[#FF6F00] to-[#C2185B]',
    },
    {
      label: isHindi ? 'Portfolio' : 'Portfolio',
      title: isHindi ? 'YouTube, Spotify, Instagram' : 'YouTube, Spotify, Instagram',
      text: isHindi ? 'Artists अपने काम के portfolio links जोड़ सकते हैं।' : 'Artists can add portfolio links to show their work.',
      accent: 'from-[#4A148C] to-[#6A1B9A]',
    },
    {
      label: isHindi ? 'PMB' : 'PMB',
      title: isHindi ? 'Private messages' : 'Private messages',
      text: isHindi ? 'Artists और collaborators login के बाद बात आगे बढ़ाते हैं।' : 'Artists and collaborators continue conversations after login.',
      accent: 'from-[#00BFA5] to-[#0F766E]',
    },
  ];

  const activityItems = liveActivityItems.length > 0 ? liveActivityItems : fallbackActivityItems;
  const audiencePaths = [
    {
      title: 'I am an Artist',
      subtitle: 'Post your profile, portfolio, photos, and performance videos.',
      action: 'Join as Artist',
      joinEvent: 'openArtistJoin',
      tone: 'from-[#FF6F00] to-[#C2185B]',
    },
    {
      title: 'I am a Collaborator',
      subtitle: 'Discover artists, send enquiries, and continue conversations in PMB.',
      action: 'Join to Connect',
      joinEvent: 'openCollaboratorJoin',
      tone: 'from-[#00BFA5] to-[#4A148C]',
    },
    {
      title: 'I want to Discover',
      subtitle: 'Browse featured and verified Indian creative talent.',
      action: 'Explore Artists',
      path: '/artists',
      tone: 'from-[#4A148C] to-[#6A1B9A]',
    },
    {
      title: 'I want AI Help',
      subtitle: 'Generate bios, captions, reel scripts, and artist statements.',
      action: 'Open AI Studio',
      path: '/ai-studio',
      tone: 'from-[#FF6F00] via-[#C2185B] to-[#4A148C]',
    },
  ];

  const eventAiCards = [
    {
      title: 'Find Event Artists',
      text: 'Tell KalaManch the event type, city, audience, budget, and vibe. Get artist roles, search filters, keywords, and a contact message.',
      points: ['Artist role suggestions', 'City/state filters', 'Contact message'],
      action: 'Find Event Artists',
      event: 'home_ai_event_finder_click',
      path: '/event-planner?mode=finder',
    },
    {
      title: 'AI Event Manager',
      text: 'Plan the lineup, stage sequence, timeline, sound-check, promotion plan, cost assumptions, and fallback checklist.',
      points: ['Lineup sequence', 'Timeline plan', 'Cost assumptions'],
      action: 'Plan My Event',
      event: 'home_ai_event_manager_click',
      path: '/event-planner?mode=manager',
    },
    {
      title: 'Find Events Near Me',
      text: 'Search public events, concerts, cultural programs, venue leads, and opportunity links artists can approach with their KalaManch profile.',
      points: ['Live events', 'Venue leads', 'Artist pitch steps'],
      action: 'Find Events',
      event: 'home_find_events_near_me_click',
      path: '/event-planner?mode=events',
    },
    {
      title: 'Creative Teams',
      text: 'Build reusable creator teams for weddings, short films, devotional events, launches, and cultural programs.',
      points: ['Role mix', 'Responsibilities', 'Invite templates'],
      action: 'Build Team',
      event: 'home_creative_teams_click',
      path: '/event-planner?mode=team',
    },
  ];

  const whyJoinCards = [
    {
      eyebrow: 'For Artists',
      hindiEyebrow: 'कलाकारों के लिए',
      title: 'Post your talent where serious collaborators can discover you.',
      hindiTitle: 'अपना हुनर ऐसी जगह पोस्ट करें जहां सही लोग आपका काम देख सकें।',
      text: 'Create a verified profile with photos, videos, Instagram, YouTube, Spotify, and portfolio details so organizers, clients, production houses, and brands can reach you.',
      hindi: 'फोटो, वीडियो, Instagram, YouTube, Spotify और पोर्टफोलियो के साथ अपनी verified profile बनाएं ताकि आयोजक, क्लाइंट, प्रोडक्शन हाउस और ब्रांड आप तक पहुंच सकें।',
      cta: 'Join as Artist',
      joinEvent: 'openArtistJoin',
      accent: 'from-[#FF6F00] via-[#C2185B] to-[#4A148C]',
      border: 'border-orange-100',
      event: 'home_why_join_artist_click',
      points: ['Verified profile', 'Portfolio links', 'Private enquiries'],
    },
    {
      eyebrow: 'For Collaborators',
      hindiEyebrow: 'सहयोगियों के लिए',
      title: 'Discover Bharat’s creative talent without scattered searching.',
      hindiTitle: 'भारतीय creative talent को आसानी से खोजें और सीधे जुड़ें।',
      text: 'Browse admin-reviewed artists, send enquiries, and continue conversations in PMB with performers, creators, writers, musicians, influencers, and stage talent.',
      hindi: 'Admin-reviewed कलाकारों को देखें, enquiries भेजें और performers, creators, writers, musicians, influencers और stage talent से PMB में बात आगे बढ़ाएं।',
      cta: 'I want to Connect / Collaborate',
      joinEvent: 'openCollaboratorJoin',
      accent: 'from-[#00BFA5] via-[#0F766E] to-[#4A148C]',
      border: 'border-teal-100',
      event: 'home_why_join_collaborator_click',
      points: ['Verified talent', 'Private messages', 'Bharat-Focused Search'],
    },
  ];

  const fallbackSuccessStories = [
    {
      label: 'Artist ready',
      title: 'A complete profile becomes a shareable portfolio.',
      text: 'Artists can bring photos, bio, city, category, Instagram, YouTube, Spotify, and performance links together in one clean public page.',
      stat: '1 profile',
      accent: 'from-[#FF6F00] to-[#C2185B]',
    },
    {
      label: 'Safer discovery',
      title: 'Admin review keeps public listings more trustworthy.',
      text: 'Profiles, media, featured status, and spotlight visibility can be reviewed before they become part of the main KalaManch experience.',
      stat: 'Admin reviewed',
      accent: 'from-[#00BFA5] to-[#0F766E]',
    },
    {
      label: 'Real conversations',
      title: 'Enquiries can move into private message boards.',
      text: 'Collaborators and artists can continue serious work discussions without exposing contact details on public artist cards.',
      stat: 'PMB ready',
      accent: 'from-[#4A148C] to-[#7C2D92]',
    },
  ];

  const homepageSuccessStories = successStories.length > 0 ? successStories : fallbackSuccessStories;

  const whoCanJoin = [
    {
      title: 'Singers & Musicians',
      text: 'Playback singers, folk artists, instrumentalists, composers, lyricists, DJs, and live performers.',
      hindi: 'गायक, संगीतकार, लोक कलाकार, वादक और लाइव performer अपना काम दिखा सकते हैं।',
      accent: 'from-orange-500 to-rose-600',
    },
    {
      title: 'Dancers',
      text: 'Classical, folk, Bollywood, contemporary, hip-hop, choreography, and movement artists.',
      hindi: 'शास्त्रीय, लोक, बॉलीवुड और modern dance कलाकार अपनी profile बना सकते हैं।',
      accent: 'from-pink-500 to-purple-700',
    },
    {
      title: 'Actors & Performers',
      text: 'Film, theatre, TV, voice-over, anchors, emcees, comedians, and stage performers.',
      hindi: 'Actor, theatre artist, voice-over artist, anchor और stage performer जुड़ सकते हैं।',
      accent: 'from-purple-700 to-indigo-700',
    },
    {
      title: 'Writers & Poets',
      text: 'Scriptwriters, dialogue writers, poets, storytellers, copywriters, lyricists, and content writers.',
      hindi: 'लेखक, कवि, गीतकार, scriptwriter और dialogue writer अपना portfolio जोड़ सकते हैं।',
      accent: 'from-amber-500 to-orange-700',
    },
    {
      title: 'Influencers & Creators',
      text: 'Instagram creators, YouTubers, reels creators, lifestyle, fashion, food, travel, and brand collaborators.',
      hindi: 'Instagram, YouTube, reels और brand collaboration करने वाले creators के लिए भी जगह है।',
      accent: 'from-teal-500 to-cyan-700',
    },
    {
      title: 'Visual & Media Artists',
      text: 'Photographers, filmmakers, video editors, cinematographers, animators, and graphic designers.',
      hindi: 'Photographer, filmmaker, editor, animator और designer अपना काम showcase कर सकते हैं।',
      accent: 'from-slate-700 to-purple-800',
    },
    {
      title: 'Folk & Cultural Artists',
      text: 'Regional performers, devotional artists, traditional instrumentalists, and cultural groups.',
      hindi: 'क्षेत्रीय, पारंपरिक, भजन, लोक और सांस्कृतिक कलाकारों के लिए खास मंच।',
      accent: 'from-lime-600 to-teal-700',
    },
    {
      title: 'Event & Stage Talent',
      text: 'Live bands, event performers, stage hosts, motivational speakers, and production-ready talent.',
      hindi: 'Event performer, live band, host और stage talent काम के अवसर पा सकते हैं।',
      accent: 'from-[#FF6F00] to-[#4A148C]',
    },
  ];

  const trustPoints = [
    {
      label: 'Verified profiles',
      text: 'Admin-reviewed artist visibility',
      hindi: 'कलाकार की प्रोफाइल admin review के बाद दिखाई जाती है।',
    },
    {
      label: 'Private messages',
      text: 'PMB for artist-collaborator conversations',
      hindi: 'कलाकार और काम देने वाले लोग private message board पर बात कर सकते हैं।',
    },
    {
      label: 'Portfolio ready',
      text: 'YouTube, Instagram, Spotify, photos, and videos',
      hindi: 'अपना फोटो, वीडियो, YouTube, Instagram और Spotify portfolio जोड़ें।',
    },
    {
      label: 'India focused',
      text: 'Built for Indian artists, organizers, and clients',
      hindi: 'भारत के गांव, शहर और metro के कलाकारों के लिए बनाया गया मंच।',
    },
  ];

  const launchTrustBadges = [
    {
      title: 'Admin-reviewed profiles',
      text: 'Artist visibility and featured status are managed by admin review.',
      accent: 'bg-orange-100 text-[#FF6F00]',
    },
    {
      title: 'Protected contact details',
      text: 'Email, phone, and social details stay away from public listing cards.',
      accent: 'bg-purple-100 text-[#4A148C]',
    },
    {
      title: 'Media safety review',
      text: 'Images and videos can be reviewed and removed from admin media tools.',
      accent: 'bg-teal-100 text-[#008C7A]',
    },
    {
      title: 'Private Message Board',
      text: 'Serious enquiries continue inside PMB instead of scattered messages.',
      accent: 'bg-amber-100 text-[#B45309]',
    },
    {
      title: 'Support & Trust reports',
      text: 'Contact and Trust & Safety requests are saved for admin follow-up.',
      accent: 'bg-rose-100 text-[#BE123C]',
    },
  ];

  const partners = [
    { name: 'Government Institution', icon: '🏛️', path: '/partners/government-institution' },
    { name: 'Music Label', icon: '🎵', path: '/partners/music-label' },
    { name: 'Production House', icon: '🎬', path: '/partners/production-house' },
    { name: 'Theater Group', icon: '🎭', path: '/partners/theater-group' }
  ];

  return (
    <div className="flex flex-col" data-testid="home-page">
      <SEO
        title="Post, Connect, Get Work"
        description="Kalamanch.art helps Indian artists post profiles, photos, videos, and portfolios so collaborators, organizers, production houses, and clients can discover and contact them."
        keywords="post artist profile, discover Indian artists, creative talent India, artist portfolio, production house artists, event artists, Kalamanch"
        imageAlt="Kalamanch.art - Bharat's digital stage for creative talent"
        schema={{
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          name: 'Kalamanch.art',
          url: 'https://www.kalamanch.art',
          description: 'India\'s digital stage for creative talent. Artists can post profiles, photos, videos, and portfolios while collaborators discover verified Indian artists.',
          potentialAction: {
            '@type': 'RegisterAction',
            target: 'https://www.kalamanch.art/join',
            name: 'Join Kala Manch',
          },
          publisher: {
            '@type': 'Organization',
            name: 'Kalamanch.art',
            url: 'https://www.kalamanch.art',
            logo: 'https://www.kalamanch.art/images/kalamanch-logo.jpg',
          },
        }}
      />
      <section
        className="gradient-bg relative order-[-20] flex items-center justify-center overflow-hidden pt-16 pb-5 text-white sm:pt-24 sm:pb-8 lg:pt-24"
        data-testid="hero-section"
      >
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-72 h-72 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-[#00BFA5] rounded-full blur-3xl"></div>
        </div>

        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-7 text-center relative z-10">
          <p className="text-xl sm:text-3xl lg:text-4xl font-bold mb-1 sm:mb-3 leading-tight text-balance" data-testid="hero-hindi-tagline">
            {pageContent.hero_tagline || 'भारत के कलाकारों का अपना मंच'}
          </p>
          <h1
            className="mx-auto max-w-[22rem] text-3xl sm:max-w-none sm:text-5xl lg:text-6xl font-bold mb-2 sm:mb-3 leading-tight text-balance"
            data-testid="hero-headline"
          >
            {pageContent.hero_title ? (
              pageContent.hero_title
            ) : isHindi ? (
              <>
                भारत के कलाकारों का अपना मंच<br />Creative Talent के लिए Digital Stage
              </>
            ) : (
              <>
                Bharat's Digital Stage<br />for Creative Talent
              </>
            )}
          </h1>
          <p
            className="text-base sm:text-xl lg:text-2xl mb-4 sm:mb-6 text-gray-100 max-w-3xl mx-auto"
            data-testid="hero-subtext"
          >
            {pageContent.hero_subtitle || (isHindi ? 'पोस्ट करें..कनेक्ट करें..काम पाएं' : 'Post..Connect..Get Work')}
            <span className="block mt-1 text-sm sm:mt-2 sm:text-lg text-white/90">
              पोस्ट करें..कनेक्ट करें..काम पाएं
            </span>
          </p>
          <div className="mx-auto mb-4 flex w-full max-w-3xl flex-col items-center justify-center gap-3 rounded-3xl border border-white/20 bg-white/14 px-3 py-4 shadow-lg backdrop-blur sm:flex-row sm:px-6">
            <div className="min-w-0 text-center sm:text-left">
              <p className="text-xs font-black uppercase tracking-[0.18em] text-orange-100 sm:text-sm sm:tracking-[0.22em]">Android App Live</p>
              <p className="mt-1 text-sm font-bold leading-6 text-white sm:text-base">Install KalaManch from Google Play and use profiles, PMB, projects, competitions, and AI Studio on mobile.</p>
            </div>
            <a
              href={ANDROID_PLAY_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackEvent('home_android_app_live_click')}
              className="block w-full max-w-[12rem] shrink-0 sm:max-w-[13rem]"
            >
              <img src="/images/store/google-play-badge.svg" alt="Get KalaManch on Google Play" className="w-full rounded-xl bg-white shadow-md" />
            </a>
          </div>
          {liveCompetition && (
            <Link
              to={`/competitions/${liveCompetition.id}`}
              onClick={() => trackEvent('home_live_competition_banner_click', { competition: liveCompetition.name })}
              className="mx-auto mb-4 flex w-full max-w-3xl flex-col items-stretch justify-between gap-3 rounded-3xl border border-emerald-200 bg-white px-3 py-4 text-left text-slate-950 shadow-xl transition hover:-translate-y-0.5 hover:shadow-2xl sm:flex-row sm:items-center sm:px-6"
              data-testid="home-live-competition-banner"
            >
              <span className="min-w-0 text-center sm:text-left">
                <span className="inline-flex max-w-full rounded-full bg-emerald-50 px-3 py-1 text-[0.68rem] font-black uppercase tracking-[0.16em] text-emerald-700 ring-1 ring-emerald-200 sm:text-xs sm:tracking-[0.18em]">
                  Live competition
                </span>
                <span className="mt-2 block text-base font-black leading-snug [overflow-wrap:anywhere] sm:text-lg">{liveCompetition.name}</span>
                <span className="mt-1 block text-sm font-semibold text-slate-600">
                  {liveCompetition.competition_date ? `Date: ${liveCompetition.competition_date}` : 'Registration and submissions are open now.'}
                </span>
              </span>
              <span className="w-full shrink-0 rounded-full bg-[#FF6F00] px-4 py-3 text-center text-sm font-extrabold leading-tight text-white shadow-lg shadow-orange-100 sm:w-auto sm:px-5">
                View Live Competition
              </span>
            </Link>
          )}
          <div className="flex flex-col justify-center gap-3 sm:flex-row sm:items-center">
            <Link to="/artists" className="w-full sm:w-auto" data-testid="explore-artists-button" onClick={() => trackEvent('home_explore_artists_click')}>
              <button className="w-full rounded-full bg-white px-8 py-4 text-base font-extrabold text-[#5B21B6] shadow-[0_18px_40px_rgba(255,255,255,0.18)] transition hover:-translate-y-0.5 hover:bg-[#FFF9F5] sm:w-auto sm:px-10 sm:text-lg">
                Explore Artists
              </button>
            </Link>
            <Link to="/collaborators" className="w-full sm:w-auto" data-testid="explore-collaborators-button" onClick={() => trackEvent('home_explore_collaborators_click')}>
              <button className="w-full rounded-full bg-white px-8 py-4 text-base font-extrabold text-[#5B21B6] shadow-[0_18px_40px_rgba(255,255,255,0.18)] transition hover:-translate-y-0.5 hover:bg-[#FFF9F5] sm:w-auto sm:px-10 sm:text-lg">
                Explore Collaborators
              </button>
            </Link>
          </div>

          <div className="relative z-30 mx-auto mt-4 grid max-w-7xl grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-7" data-testid="home-platform-actions">
            {platformActions.map((action) => {
              const Icon = action.icon;
              const content = (
                <>
                  <span className="mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-white text-[#C2185B] shadow-sm">
                    <Icon className="h-5 w-5" aria-hidden="true" />
                  </span>
                  <span className="mt-2 block whitespace-nowrap text-sm font-extrabold leading-tight text-white">{action.label}</span>
                  <span className="pointer-events-none absolute left-1/2 top-full z-50 mt-3 hidden w-[14rem] -translate-x-1/2 rounded-2xl border border-white/25 bg-[#1F1235] px-3 py-2 text-xs font-semibold leading-5 text-white shadow-2xl lg:block lg:opacity-0 lg:transition lg:duration-200 lg:group-hover:opacity-100 lg:group-focus-within:opacity-100">
                    {action.description}
                  </span>
                </>
              );
              const classes = "group relative z-0 rounded-2xl border border-white/25 bg-white/14 px-3 py-3 text-center shadow-sm backdrop-blur transition hover:z-40 hover:-translate-y-0.5 hover:bg-white/22 focus-visible:z-40 focus-within:z-40";
              return action.to ? (
                <Link key={action.label} to={action.to} onClick={() => trackEvent(action.event)} className={classes} title={action.description} aria-label={`${action.label}. ${action.description}`}>
                  {content}
                </Link>
              ) : (
                <button key={action.label} type="button" onClick={action.onClick} className={classes} title={action.description} aria-label={`${action.label}. ${action.description}`}>
                  {content}
                </button>
              );
            })}
          </div>

          <div className="relative z-10 mx-auto mt-5 w-full max-w-5xl rounded-[2rem] border border-white/20 bg-white/14 p-4 shadow-lg backdrop-blur sm:p-5">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div className="min-w-0">
                <p className="text-xs font-black uppercase tracking-[0.18em] text-orange-100 sm:text-sm sm:tracking-[0.22em]">Search for artists</p>
                <h2 className="mt-2 text-xl font-black text-white sm:text-2xl">Discover talent before you sign up</h2>
                <p className="mt-2 max-w-2xl text-sm leading-6 text-white/85 sm:text-base">
                  Search by art form, city, artist name, style, or event need. Visitors can browse results now. Contact details and social links unlock after signup and login.
                </p>
              </div>
              <Link
                to={homeSearchQuery.trim() ? `/artists?q=${encodeURIComponent(homeSearchQuery.trim())}` : '/artists'}
                onClick={() => trackEvent('home_search_open_directory_click', { query: homeSearchQuery.trim() })}
                className="inline-flex shrink-0 items-center justify-center rounded-full bg-white px-5 py-3 text-sm font-extrabold text-[#5B21B6] shadow-md transition hover:-translate-y-0.5 hover:bg-[#FFF9F5]"
              >
                Open full artist directory
              </Link>
            </div>

            <div className="mt-4 flex flex-col gap-3 sm:flex-row">
              <label className="relative block flex-1">
                <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-500" />
                <input
                  type="text"
                  value={homeSearchQuery}
                  onChange={(event) => setHomeSearchQuery(event.target.value)}
                  placeholder="Search singer, rangoli artist, poet, photographer, Mumbai, Varanasi..."
                  className="h-14 w-full rounded-2xl border border-white/30 bg-white px-12 text-sm font-medium text-slate-900 outline-none ring-0 placeholder:text-slate-400 focus:border-orange-200 focus:shadow-[0_0_0_3px_rgba(255,111,0,0.12)] sm:text-base"
                />
              </label>
              <Link
                to={homeSearchQuery.trim() ? `/artists?q=${encodeURIComponent(homeSearchQuery.trim())}` : '/artists'}
                onClick={() => trackEvent('home_search_submit_click', { query: homeSearchQuery.trim() })}
                className="inline-flex h-14 items-center justify-center rounded-2xl bg-[#FF6F00] px-6 text-sm font-extrabold text-white shadow-lg shadow-orange-100 transition hover:-translate-y-0.5 sm:text-base"
              >
                Search artists
              </Link>
            </div>

            {homeSearchQuery.trim() ? (
              <div className="mt-4">
                {loadingHomeSearch ? (
                  <div className="rounded-2xl border border-white/20 bg-white/12 px-4 py-4 text-sm font-semibold text-white/80">
                    Loading artists...
                  </div>
                ) : homeArtistMatches.length > 0 ? (
                  <>
                    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                      {homeArtistMatches.map((artist) => (
                        <Link
                          key={artist.id || artist.name}
                          to={artistProfilePath(artist)}
                          onClick={() => trackEvent('home_search_artist_click', { artist: artist.name, query: homeSearchQuery.trim() })}
                          className="rounded-[1.75rem] border border-white/20 bg-white/96 p-4 text-left text-slate-950 shadow-md transition hover:-translate-y-0.5 hover:shadow-xl"
                        >
                          <div className="flex items-start gap-4">
                            <span className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-2xl border border-orange-100 bg-orange-50 text-xl font-black text-[#FF6F00]">
                              {artist.image_url ? (
                                <img src={artist.image_url} alt={`${artist.name} artist`} className="h-full w-full object-cover" loading="lazy" />
                              ) : (
                                (artist.name || 'A').charAt(0).toUpperCase()
                              )}
                            </span>
                            <span className="min-w-0 flex-1">
                              <span className="block truncate text-lg font-black text-slate-950">{artist.name}</span>
                              <span className="mt-1 block truncate text-sm font-semibold text-[#B45309]">
                                {artist.display_profession || artist.category || artist.subcategory || 'Artist'}
                              </span>
                              <span className="mt-2 flex items-center gap-1 text-sm text-slate-700">
                                <MapPin className="h-4 w-4 shrink-0 text-[#FF6F00]" />
                                <span className="truncate">{artist.city || artist.state || 'India'}</span>
                              </span>
                            </span>
                          </div>
                          <p className="mt-3 line-clamp-2 min-h-[3rem] text-sm leading-6 text-slate-700">
                            {artist.bio || artist.smart_fit || 'View profile, portfolio, and availability on KalaManch.'}
                          </p>
                          <div className="mt-4 flex items-center justify-between gap-3">
                            <span className="rounded-full bg-orange-50 px-3 py-1 text-xs font-bold text-[#4A148C]">
                              {artist.is_verified ? 'Verified on KalaManch' : 'Profile available'}
                            </span>
                            <span className="text-sm font-extrabold text-[#4C1D95]">View profile</span>
                          </div>
                        </Link>
                      ))}
                    </div>
                    <div className="mt-4 flex justify-center">
                      <Link
                        to={`/artists?q=${encodeURIComponent(homeSearchQuery.trim())}`}
                        onClick={() => trackEvent('home_search_more_results_click', { query: homeSearchQuery.trim() })}
                        className="inline-flex items-center justify-center rounded-full border border-white/25 bg-white/14 px-5 py-2.5 text-sm font-extrabold text-white shadow-sm transition hover:bg-white/18"
                      >
                        View all results
                      </Link>
                    </div>
                  </>
                ) : (
                  <div className="rounded-2xl border border-white/20 bg-white/12 px-4 py-4 text-sm font-semibold text-white/85">
                    No close matches yet. Try a broader search like singer, rangoli, poet, photographer, or a city name.
                  </div>
                )}
              </div>
            ) : null}
          </div>

          <div className="mx-auto mt-6 flex max-w-5xl flex-wrap justify-center gap-2.5" data-testid="home-discipline-chips">
            {disciplineChips.map((chip) => (
              <Link
                key={chip.label}
                to={`/artists?q=${encodeURIComponent(chip.query)}`}
                onClick={() => trackEvent('home_discipline_chip_click', { discipline: chip.label })}
                className="rounded-full border border-white/20 bg-white/12 px-4 py-2 text-sm font-semibold text-white shadow-sm backdrop-blur transition hover:bg-white/18"
              >
                {chip.label}
              </Link>
            ))}
          </div>
        </div>
      </section>
      <section className="order-[-18] bg-gray-50 px-4 pt-2 sm:px-6 lg:px-8" data-testid="home-ai-mentor-pro-banner">
        <Link
          to="/ai-mentor"
          onClick={() => trackEvent('home_ai_mentor_pro_banner_click')}
          className="mx-auto flex w-full max-w-7xl flex-col items-center justify-between gap-4 rounded-[2rem] border border-orange-100 bg-white px-5 py-5 text-left text-slate-950 shadow-xl transition hover:-translate-y-0.5 hover:shadow-2xl sm:flex-row sm:px-7"
        >
          <span className="flex min-w-0 items-center gap-4 text-center sm:text-left">
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#4A148C] text-white shadow-md">
              <Crown className="h-5 w-5" />
            </span>
            <span className="min-w-0">
              <span className="block text-xs font-black uppercase tracking-[0.18em] text-[#FF6F00]">Paid AI Mentor Pro</span>
              <span className="mt-1 block text-base font-black leading-snug sm:text-lg">
                Request early access for critique, drills, market insight, pricing guidance, and a 12-month artist growth plan.
              </span>
            </span>
          </span>
          <span className="w-full shrink-0 rounded-full bg-[#FF6F00] px-6 py-3 text-center text-sm font-extrabold text-white shadow-lg shadow-orange-100 sm:w-auto">
            Request Access
          </span>
        </Link>
      </section>
      <section className="relative z-20 order-[-18] mt-8 bg-transparent px-4 sm:px-6 lg:px-8" data-testid="why-join-section">
        <div className="mx-auto max-w-7xl overflow-hidden rounded-[2.25rem] border border-orange-100 bg-white shadow-2xl">
          <div className="grid gap-0 lg:grid-cols-[0.75fr_1.25fr]">
            <div className="relative overflow-hidden bg-gradient-to-br from-[#2A0F5E] via-[#4A148C] to-[#FF6F00] p-8 text-white sm:p-10">
              <div className="absolute -left-16 -top-16 h-44 w-44 rounded-full bg-white/10 blur-2xl" />
              <div className="absolute -bottom-20 right-0 h-56 w-56 rounded-full bg-[#00BFA5]/25 blur-3xl" />
              <div className="relative">
                <p className="text-sm font-bold uppercase tracking-[0.3em] text-white/75">Why join?</p>
                <h2 className="mt-4 text-3xl font-bold leading-tight sm:text-4xl">
                  Why Join KalaManch?
                </h2>
                <p className="mt-5 max-w-md leading-7 text-white/85">
                  One platform for artists to post work, collaborators to discover talent, and both sides to move from interest to real creative conversations.
                </p>
              </div>
            </div>

            <div className="grid gap-5 bg-[#fffaf4] p-5 sm:p-6 lg:grid-cols-2 lg:p-7">
              {whyJoinCards.map((card) => (
                <article key={card.eyebrow} className={`flex h-full flex-col rounded-[2rem] border ${card.border} bg-white p-6 shadow-sm`}>
                  <div className={`mb-5 h-2 w-24 rounded-full bg-gradient-to-r ${card.accent}`} />
                  <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#FF6F00]">{card.eyebrow}</p>
                  <p className="mt-1 text-sm font-semibold text-[#4A148C]">{card.hindiEyebrow}</p>
                  <h3 className="mt-4 text-2xl font-bold leading-tight text-gray-900">{card.title}</h3>
                  <p className="mt-4 leading-7 text-gray-600">{card.text}</p>
                  <div className="mt-5 flex flex-wrap gap-2">
                    {card.points.map((point) => (
                      <span key={point} className="rounded-full bg-orange-50 px-3 py-1 text-xs font-bold text-[#4A148C]">
                        {point}
                      </span>
                    ))}
                  </div>
                  <button
                    type="button"
                    className="btn-primary mt-auto w-full"
                    onClick={() => {
                      trackEvent(card.event, { location: 'why_join_section' });
                      if (card.joinEvent === 'openArtistJoin') {
                        openArtistJoinOrAccount();
                      } else {
                        openCollaboratorJoinOrAccount();
                      }
                    }}
                  >
                    {card.cta}
                  </button>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="hidden" data-testid="live-activity-section">
        <div className="mx-auto max-w-7xl rounded-[2rem] border border-orange-100 bg-white/95 p-4 shadow-2xl backdrop-blur">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center">
            <div className="shrink-0 rounded-2xl bg-gradient-to-br from-[#FF6F00] to-[#4A148C] px-5 py-4 text-white">
              <p className="text-xs font-bold uppercase tracking-[0.24em] text-white/75">{isHindi ? 'Live Updates' : 'Live Updates'}</p>
              <h2 className="mt-1 text-xl font-bold">{isHindi ? 'KalaManch अभी active है' : 'KalaManch is active now'}</h2>
            </div>
            <div className="grid flex-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
              {activityItems.map((item, index) => (
                <article key={`${item.label}-${item.title}-${index}`} className="rounded-2xl border border-orange-100 bg-[#fffaf4] p-4">
                  <div className={`mb-3 h-1.5 w-16 rounded-full bg-gradient-to-r ${item.accent}`} />
                  <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-gray-500">{item.label}</p>
                  <h3 className="mt-1 line-clamp-1 text-base font-bold text-gray-900">{item.title}</h3>
                  <p className="mt-1 line-clamp-2 text-sm leading-5 text-gray-600">{item.text}</p>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="order-[-16] bg-white py-10 sm:py-12" data-testid="ai-event-tools-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="overflow-hidden rounded-[2.25rem] border border-orange-100 bg-gradient-to-br from-slate-950 via-[#25104F] to-[#FF6F00] p-5 text-white shadow-2xl sm:p-8">
            <div className="grid gap-8 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
              <div>
                <p className="text-sm font-bold uppercase tracking-[0.3em] text-orange-200">For organizers</p>
                <h2 className="mt-3 text-3xl font-black leading-tight sm:text-4xl">
                  Plan creative events with KalaManch AI
                </h2>
                <p className="mt-4 max-w-xl text-base leading-7 text-white/82">
                  Use Find Event Artists, AI Event Manager, and Creative Teams to move from a rough event idea to artist search, lineup planning, team roles, and a practical checklist.
                </p>
                <Link
                  to="/event-planner"
                  onClick={() => trackEvent('home_ai_event_tools_open_studio')}
                  className="mt-6 inline-flex rounded-full bg-white px-6 py-3 text-sm font-extrabold text-[#4A148C] shadow-lg transition hover:-translate-y-0.5 hover:bg-orange-50"
                >
                  Open Event Planner
                </Link>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                {eventAiCards.map((card) => (
                  <article key={card.title} className="flex h-full flex-col rounded-[1.75rem] border border-white/15 bg-white/12 p-5 shadow-lg backdrop-blur transition hover:-translate-y-1 hover:bg-white/16">
                    <div className="flex items-start justify-between gap-3">
                      <p className="text-xs font-bold uppercase tracking-[0.22em] text-orange-200">AI tool</p>
                      <span className="rounded-full bg-white/12 px-2.5 py-1 text-[11px] font-black text-white/90">{card.points[0]}</span>
                    </div>
                    <h3 className="mt-3 text-2xl font-black leading-tight text-white">{card.title}</h3>
                    <p className="mt-3 text-sm leading-6 text-white/82">{card.text}</p>
                    <div className="mt-5 flex flex-wrap content-start gap-2">
                      {card.points.map((point) => (
                        <span key={point} className="rounded-full bg-white/14 px-3 py-1 text-xs font-bold text-white">
                          {point}
                        </span>
                      ))}
                    </div>
                    <div className="mt-auto pt-6">
                      <Link
                        to={card.path}
                        onClick={() => trackEvent(card.event)}
                        className="inline-flex min-h-11 w-full items-center justify-center rounded-full border border-white/35 px-5 py-2.5 text-center text-sm font-extrabold text-white transition hover:bg-white/12 sm:w-fit"
                      >
                        {card.action}
                      </Link>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="order-[-19] bg-gray-50 px-4 pb-16 pt-16 sm:px-6 sm:pb-20 sm:pt-20 lg:px-8" data-testid="featured-artists-section">
        <div className="mx-auto max-w-7xl">
          <div className="mx-auto mb-9 max-w-3xl text-center sm:mb-10">
            <p className="mb-4 text-sm font-bold uppercase tracking-[0.3em] text-[#00BFA5]">
              {isHindi ? 'Admin verified और featured' : 'Admin verified and featured'}
            </p>
            <h2 className="launch-heading mx-auto text-4xl font-bold gradient-text sm:text-5xl" data-testid="featured-artists-heading">
              {isHindi ? 'चुनिंदा कलाकार' : 'Artists in Focus'}
            </h2>
            <p className="launch-copy mx-auto mt-4 max-w-2xl text-lg text-gray-600" data-testid="featured-artists-description">
              {isHindi ? 'चुने हुए performers, creators और artists देखें जो आपके project को life दे सकते हैं।' : 'Discover selected performers, creators, and artists ready to bring your vision to life.'}
            </p>
          </div>
          {loadingArtists ? (
            <div className="text-center py-12 text-gray-500">{isHindi ? 'कलाकार लोड हो रहे हैं...' : 'Loading artists...'}</div>
          ) : featuredRoster.length > 0 ? (
            <>
              <div className="mb-12" data-testid="featured-artist-spotlight">
                <div className="mb-7 text-center">
                  <p className="text-sm font-bold uppercase tracking-[0.3em] text-[#FF6F00]">Top showcase</p>
                  <h3 className="mt-2 text-3xl font-black text-gray-950 sm:text-4xl">Spotlight Artists</h3>
                  <p className="mx-auto mt-2 max-w-2xl text-gray-600">Three standout artists selected for the highest homepage visibility.</p>
                </div>
                <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
                  {spotlightArtists.map((artist) => (
                    <RegisteredArtistCard key={artist.id || artist.name} artist={artist} spotlight />
                  ))}
                </div>
              </div>

              {standardFeaturedArtists.length > 0 && (
                <div className="border-t border-gray-200 pt-10">
                  <div className="mb-7 text-center">
                    <p className="text-sm font-bold uppercase tracking-[0.3em] text-[#00BFA5]">Selected profiles</p>
                    <h3 className="mt-2 text-2xl font-black text-gray-900 sm:text-3xl">Featured Artists</h3>
                    <p className="mx-auto mt-2 max-w-2xl text-sm text-gray-600">More admin-verified artists ready for creative opportunities.</p>
                  </div>
                  <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
                    {standardFeaturedArtists.map((artist) => (
                      <RegisteredArtistCard key={artist.id || artist.name} artist={artist} compact />
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12 text-gray-500">{isHindi ? 'अभी कोई featured artist उपलब्ध नहीं है।' : 'No featured artists available yet.'}</div>
          )}
          <div className="text-center mt-12">
            <Link to="/artists" data-testid="view-all-artists-button" onClick={() => trackEvent('home_view_all_artists_click')}>
              <button className="btn-secondary">
                {isHindi ? 'सभी कलाकार देखें' : 'View All Artists'}
              </button>
            </Link>
          </div>

          {featuredCollaborators.length > 0 && (
            <div className="mt-12 border-t border-gray-200 pt-10" data-testid="featured-collaborators-section">
              <div className="mb-8 flex flex-col gap-4 text-left sm:flex-row sm:items-end sm:justify-between">
                <div>
                  <p className="text-sm font-bold uppercase tracking-[0.3em] text-[#FF6F00]">
                    Collaborators
                  </p>
                  <h2 className="launch-heading mt-3 max-w-3xl text-3xl font-bold text-gray-950 sm:text-4xl">
                    Collaborators hiring creative talent
                  </h2>
                  <p className="launch-copy mt-3 max-w-2xl text-gray-600">
                    Organizations, creative teams, and clients looking for artists on KalaManch.
                  </p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-black text-emerald-800">
                      Institutes & Academies
                    </span>
                    <span className="rounded-full bg-fuchsia-50 px-3 py-1 text-xs font-black text-fuchsia-800">
                      Studios & Venues
                    </span>
                    <span className="rounded-full bg-orange-50 px-3 py-1 text-xs font-black text-[#FF6F00]">
                      Event & Talent Teams
                    </span>
                  </div>
                </div>
                <div className="flex w-full items-center gap-2 sm:w-auto">
                  {featuredCollaborators.length > 3 && (
                    <div className="flex shrink-0 items-center gap-2" aria-label="Collaborator carousel controls">
                      <button
                        type="button"
                        onClick={() => scrollCollaborators(-1)}
                        className="flex h-11 w-11 items-center justify-center rounded-full border border-orange-200 bg-white text-[#FF6F00] shadow-sm transition hover:-translate-y-0.5 hover:bg-orange-50 hover:shadow-md"
                        aria-label="Show previous collaborators"
                        title="Previous collaborators"
                      >
                        <ChevronLeft className="h-5 w-5" aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        onClick={() => scrollCollaborators(1)}
                        className="flex h-11 w-11 items-center justify-center rounded-full bg-[#FF6F00] text-white shadow-md transition hover:-translate-y-0.5 hover:bg-[#E86300] hover:shadow-lg"
                        aria-label="Show more collaborators"
                        title="Next collaborators"
                      >
                        <ChevronRight className="h-5 w-5" aria-hidden="true" />
                      </button>
                    </div>
                  )}
                  <Link
                    to="/collaborators"
                    className="btn-outline min-w-0 flex-1 text-center sm:flex-none"
                    onClick={() => trackEvent('home_view_all_collaborators_click')}
                  >
                    View All Collaborators
                  </Link>
                </div>
              </div>

              <div
                ref={collaboratorCarouselRef}
                className={featuredCollaborators.length <= 3
                  ? 'flex justify-center pb-3'
                  : '-mx-4 snap-x snap-mandatory overflow-x-auto scroll-smooth px-4 pb-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden'}
                aria-label="Featured collaborators"
              >
                <div className={featuredCollaborators.length <= 3 ? 'flex max-w-full flex-wrap justify-center gap-5' : 'flex gap-5'}>
                  {featuredCollaborators.map((collaborator) => (
                    <div key={collaborator.id || collaborator.slug} className="h-[304px] snap-start" data-collaborator-carousel-card>
                      <CollaboratorHomeCard collaborator={collaborator} />
                    </div>
                  ))}
                </div>
              </div>

              {(featuredInstituteCollaborators.length > 0 || featuredStudioCollaborators.length > 0) && (
                <div className="mt-8 grid gap-6 xl:grid-cols-2">
                  {featuredInstituteCollaborators.length > 0 && (
                    <div className="rounded-[2rem] border border-emerald-100 bg-emerald-50/45 p-5">
                      <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
                        <div>
                          <p className="text-xs font-black uppercase tracking-[0.22em] text-emerald-700">Institutes & Academies</p>
                          <h3 className="mt-2 text-2xl font-black text-gray-950">Training and talent development partners</h3>
                        </div>
                        <Link to="/collaborators" className="text-sm font-black text-emerald-800 hover:text-emerald-950">
                          Explore all
                        </Link>
                      </div>
                      <div className="grid gap-4">
                        {featuredInstituteCollaborators.map((collaborator) => (
                          <CollaboratorHomeCard key={`institute-${collaborator.id || collaborator.slug}`} collaborator={collaborator} />
                        ))}
                      </div>
                    </div>
                  )}
                  {featuredStudioCollaborators.length > 0 && (
                    <div className="rounded-[2rem] border border-fuchsia-100 bg-fuchsia-50/35 p-5">
                      <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
                        <div>
                          <p className="text-xs font-black uppercase tracking-[0.22em] text-fuchsia-700">Studios & Venues</p>
                          <h3 className="mt-2 text-2xl font-black text-gray-950">Spaces for shoots, rehearsals, workshops, and shows</h3>
                        </div>
                        <Link to="/collaborators" className="text-sm font-black text-fuchsia-800 hover:text-fuchsia-950">
                          Explore all
                        </Link>
                      </div>
                      <div className="grid gap-4">
                        {featuredStudioCollaborators.map((collaborator) => (
                          <CollaboratorHomeCard key={`studio-${collaborator.id || collaborator.slug}`} collaborator={collaborator} />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {SHOW_MARKETING_HOME_SECTIONS && (
      <section className="bg-white py-10 sm:py-12" data-testid="success-stories-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="overflow-hidden rounded-[2.25rem] border border-orange-100 bg-gradient-to-br from-[#fffaf4] via-white to-purple-50 p-6 shadow-xl sm:p-8">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <p className="text-sm font-bold uppercase tracking-[0.3em] text-[#FF6F00]">Early wins</p>
                <h2 className="mt-3 text-3xl font-bold text-gray-900 sm:text-4xl">
                  Success stories on KalaManch
                </h2>
                <p className="mt-3 max-w-3xl text-gray-600">
                  Admin-managed proof points that show artists and collaborators how the platform helps them move from profile to opportunity.
                </p>
              </div>
              <button
                type="button"
                className="btn-outline"
                onClick={() => {
                  trackEvent('home_success_stories_join_click');
                  openArtistJoinOrAccount();
                }}
              >
                Create your story
              </button>
            </div>

            <div className="mt-7 grid gap-5 md:grid-cols-3">
              {homepageSuccessStories.slice(0, 3).map((story) => (
                <article key={story.id || story.title} className="rounded-[1.75rem] border border-white bg-white p-6 shadow-sm">
                  <div className={`mb-5 h-2 w-24 rounded-full bg-gradient-to-r ${story.accent || 'from-[#FF6F00] to-[#C2185B]'}`} />
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="rounded-full bg-orange-50 px-3 py-1 text-xs font-bold uppercase tracking-[0.18em] text-[#FF6F00]">
                      {story.label}
                    </span>
                    {story.stat && (
                      <span className="rounded-full bg-teal-50 px-3 py-1 text-xs font-bold text-[#008C7A]">
                        {story.stat}
                      </span>
                    )}
                  </div>
                  <h3 className="mt-5 text-xl font-bold leading-tight text-gray-900">{story.title}</h3>
                  <p className="mt-3 leading-7 text-gray-600">{story.text}</p>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>
      )}

      {SHOW_MARKETING_HOME_SECTIONS && (
      <section className="bg-[#fff8f1] py-14 sm:py-16" data-testid="launch-paths-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <p className="text-sm font-bold uppercase tracking-[0.3em] text-[#FF6F00]">{isHindi ? 'यहां से शुरू करें' : 'Start here'}</p>
            <h2 className="launch-heading mx-auto mt-3 max-w-3xl text-3xl sm:text-5xl font-bold gradient-text">
              {isHindi ? 'एक platform. तीन आसान रास्ते.' : 'One platform. Three clear paths.'}
            </h2>
            <p className="launch-copy mt-4 text-gray-600 max-w-2xl mx-auto">
        {isHindi ? 'आप artist हों, collaborator हों, या कलाकारों को discover करना चाहते हों, अगला कदम साफ है।' : 'Whether you want to post your talent, connect with talent, or simply discover artists, the next step is clear.'}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
            {audiencePaths.map((item) => (
              <Link
                key={item.title}
                to={item.path || '/'}
                onClick={(event) => {
                  trackEvent('home_audience_path_click', { path_title: item.title });
                  if (item.joinEvent) {
                    event.preventDefault();
                    if (item.joinEvent === 'openArtistJoin') {
                      openArtistJoinOrAccount();
                    } else {
                      openCollaboratorJoinOrAccount();
                    }
                  }
                }}
                className="group flex h-full min-h-[20rem] flex-col rounded-[2rem] bg-white p-7 shadow-lg ring-1 ring-orange-100 transition duration-200 hover:-translate-y-1 hover:shadow-2xl"
              >
                <div className={`mb-6 h-2 w-24 rounded-full bg-gradient-to-r ${item.tone}`} />
                <h3 className="text-2xl font-bold text-gray-900">{item.title}</h3>
                <p className="mt-3 min-h-[6rem] text-gray-600 leading-7">{item.subtitle}</p>
                <div className="mt-auto inline-flex min-h-12 w-full items-center justify-center rounded-full bg-orange-50 px-5 py-3 text-center text-sm font-bold text-[#FF6F00] group-hover:bg-[#FF6F00] group-hover:text-white sm:w-fit">
                  {item.action}
                  <span className="ml-2">-&gt;</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
      )}

      {SHOW_EXTENDED_HOME_SECTIONS && (
      <section className="bg-white py-14 sm:py-16" data-testid="who-can-join-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid gap-8 lg:grid-cols-[0.7fr_1.3fr] lg:items-start">
            <div className="lg:sticky lg:top-28">
              <p className="text-sm font-bold uppercase tracking-[0.3em] text-[#00BFA5]">{isHindi ? 'कौन जुड़ सकता है?' : 'Who can join?'}</p>
              <h2 className="launch-heading mt-3 text-3xl sm:text-5xl font-bold gradient-text">
                {isHindi ? 'भारत के creative talent के लिए एक मंच।' : "A platform for Bharat's creative talent."}
              </h2>
              <p className="launch-copy mt-4 text-gray-600">
                {isHindi
                  ? 'Facebook, WhatsApp या search से आने वाले कलाकार तुरंत समझें कि यह platform गांव, शहर और metro के artists के लिए है।'
                  : 'Whether someone sees Kala Manch from Facebook, WhatsApp, or search, they should immediately know: this platform is for artists from villages, cities, and metros.'}
              </p>
              <button
                type="button"
                onClick={() => {
                  trackEvent('home_who_can_join_cta_click');
                  window.dispatchEvent(new Event('openArtistJoin'));
                }}
                data-testid="who-can-join-cta"
                className="btn-primary mt-7"
              >
                {isHindi ? 'Kala Manch से जुड़ें' : 'Join Kala Manch'}
              </button>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              {whoCanJoin.map((item) => (
                <article
                  key={item.title}
                  className="group overflow-hidden rounded-[1.75rem] border border-orange-100 bg-white shadow-lg transition duration-200 hover:-translate-y-1 hover:shadow-2xl"
                >
                  <div className={`h-2 bg-gradient-to-r ${item.accent}`} />
                  <div className="p-5 sm:p-6">
                    <div className={`mb-5 h-14 w-14 rounded-2xl bg-gradient-to-br ${item.accent} opacity-95 shadow-lg`} />
                    <h3 className="text-xl font-bold text-gray-900">{item.title}</h3>
                    <p className="mt-3 text-sm leading-6 text-gray-600">{item.text}</p>
                    <p className="mt-3 border-t border-orange-100 pt-3 text-sm leading-6 text-gray-700">{item.hindi}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>
      )}

      {SHOW_EXTENDED_HOME_SECTIONS && (
      <section className="bg-white py-12" data-testid="trust-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-[0.85fr_1.15fr] gap-8 items-center rounded-[2rem] border border-orange-100 bg-gradient-to-br from-orange-50 via-white to-purple-50 p-6 sm:p-8 shadow-lg">
            <div>
              <p className="text-sm font-bold uppercase tracking-[0.3em] text-[#4A148C]">{isHindi ? 'Kala Manch क्यों?' : 'Why Kala Manch'}</p>
              <h2 className="launch-heading mt-3 text-3xl sm:text-4xl font-bold text-gray-900">
                {isHindi ? 'Serious creative discovery के लिए बनाया गया।' : 'Built for serious creative discovery.'}
              </h2>
              <p className="launch-copy mt-4 text-gray-600">
                {isHindi
                  ? 'Profiles, media, enquiries और portfolio links organized रहते हैं ताकि artists और collaborators भरोसे से connect कर सकें।'
                  : 'The launch version keeps profiles, media, enquiries, and portfolio links organized so artists and collaborators can connect with more confidence.'}
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {trustPoints.map((point) => (
                <div key={point.label} className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-orange-100">
                  <h3 className="font-bold text-gray-900">{point.label}</h3>
                  <p className="mt-2 text-sm leading-6 text-gray-600">{point.text}</p>
                  <p className="mt-3 border-t border-orange-100 pt-3 text-sm leading-6 text-gray-700">
                    {point.hindi}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
      )}

      {sponsors.length > 0 && (
        <section className="bg-[#fffaf4] py-14 sm:py-16" data-testid="brand-partners-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between mb-10">
              <div>
                <p className="text-sm font-bold uppercase tracking-[0.3em] text-[#FF6F00]">Brand partners</p>
                <h2 className="launch-heading mt-3 max-w-3xl text-3xl sm:text-5xl font-bold gradient-text">
                  {pageContent.brand_heading || 'Brands supporting creative talent'}
                </h2>
              </div>
            </div>

            <div className={`grid gap-5 ${sponsors.length === 1 ? 'mx-auto max-w-xl grid-cols-1' : 'md:grid-cols-2 xl:grid-cols-3'}`}>
              {sponsors.map((sponsor) => {
                const card = (
                  <article className="h-full rounded-[2rem] border border-orange-100 bg-white p-6 shadow-lg transition duration-200 hover:-translate-y-1 hover:shadow-2xl">
                    <div className="mb-5 flex items-center justify-between gap-4">
                      <div className="flex h-16 w-24 items-center justify-center rounded-2xl bg-orange-50">
                        {sponsor.logo_url ? (
                          <img src={sponsor.logo_url} alt={`${sponsor.brand_name} logo`} className="max-h-12 max-w-20 object-contain" />
                        ) : (
                          <span className="text-2xl font-bold text-[#FF6F00]">{sponsor.brand_name?.charAt(0) || 'B'}</span>
                        )}
                      </div>
                      <span className="rounded-full bg-purple-100 px-3 py-1 text-xs font-bold uppercase tracking-wide text-[#4A148C]">
                        {sponsor.placement_label || 'Brand Partner'}
                      </span>
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900">{sponsor.brand_name}</h3>
                    <p className="mt-3 font-semibold text-[#FF6F00]">{sponsor.headline}</p>
                    {sponsor.description && <p className="mt-3 leading-7 text-gray-600">{sponsor.description}</p>}
                    {sponsor.link_url && <p className="mt-5 text-sm font-bold text-[#4A148C]">Learn more -&gt;</p>}
                  </article>
                );

                return sponsor.link_url ? (
                  <a
                    key={sponsor.id}
                    href={sponsor.link_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => trackEvent('home_sponsor_click', { sponsor: sponsor.brand_name })}
                  >
                    {card}
                  </a>
                ) : (
                  <div key={sponsor.id}>{card}</div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      <section className="section-padding bg-white" data-testid="video-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8 text-center">
            <h2 className="launch-heading mx-auto max-w-3xl text-4xl sm:text-5xl font-bold gradient-text mb-4" data-testid="video-section-heading">
              Performance Showcase
            </h2>
            <p className="launch-copy text-lg text-gray-600 max-w-2xl mx-auto" data-testid="video-section-description">
          {isHindi ? 'Admin द्वारा approved artists के YouTube performances देखें।' : 'Watch admin-approved YouTube performances from KalaManch artists.'}
            </p>
          </div>
          {youtubeVideos.length > 0 ? (
            <>
              <div className="mb-4 flex items-center justify-between gap-4">
                <p className="text-sm font-medium text-gray-500">Swipe, drag, or use the arrows to browse</p>
                <div className="flex shrink-0 items-center gap-2" aria-label="Performance carousel controls">
                  <button
                    type="button"
                    onClick={() => scrollPerformances(-1)}
                    className="flex h-10 w-10 items-center justify-center rounded-full border border-orange-200 bg-white text-[#FF6F00] shadow-sm transition hover:-translate-y-0.5 hover:bg-orange-50 hover:shadow-md"
                    aria-label="Show previous performances"
                    data-testid="performance-scroll-previous"
                  >
                    <ChevronLeft className="h-5 w-5" aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    onClick={() => scrollPerformances(1)}
                    className="flex h-10 w-10 items-center justify-center rounded-full bg-[#FF6F00] text-white shadow-md transition hover:-translate-y-0.5 hover:bg-[#E86300] hover:shadow-lg"
                    aria-label="Show more performances"
                    data-testid="performance-scroll-next"
                  >
                    <ChevronRight className="h-5 w-5" aria-hidden="true" />
                  </button>
                </div>
              </div>
              <div
                ref={performanceCarouselRef}
                className="kalamanch-horizontal-scroll flex touch-pan-x snap-x snap-proximity items-start gap-4 overflow-x-auto scroll-smooth pb-5"
                aria-label="Artist performance videos"
                data-testid="performance-carousel"
                tabIndex={0}
              >
                {youtubeVideos.map((video, index) => (
                  <div
                    key={`${video.id}-${index}`}
                    className="w-[17rem] shrink-0 snap-start overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-md hover-lift sm:w-[19rem]"
                    data-testid={`video-card-${index}`}
                  >
                    <div className="aspect-video">
                      <iframe
                        loading="lazy"
                        width="100%"
                        height="100%"
                        src={`https://www.youtube.com/embed/${video.id}`}
                        title={video.title}
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        data-testid={`youtube-iframe-${index}`}
                      ></iframe>
                    </div>
                    <div className="bg-white p-3.5">
                      <h3 className="line-clamp-1 break-words text-sm font-bold leading-5 text-gray-800" data-testid={`video-title-${index}`}>
                        {video.artistName}
                      </h3>
                      {video.category && <p className="mt-1 line-clamp-2 min-h-[2.5rem] text-xs leading-5 text-gray-500">{video.category}</p>}
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="rounded-3xl border border-orange-100 bg-orange-50/60 p-8 text-center text-gray-600">
            {isHindi ? 'Admin approval के बाद artists के YouTube performances यहां दिखेंगे।' : 'Artist YouTube performances will appear here once approved by admin.'}
            </div>
          )}
        </div>
      </section>

      <section className="section-padding bg-gradient-to-br from-orange-50 to-purple-50" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="launch-heading mx-auto max-w-3xl text-4xl sm:text-5xl font-bold gradient-text mb-4" data-testid="how-it-works-heading">
              {isHindi ? 'कैसे काम करता है' : 'How It Works'}
            </h2>
            <p className="launch-copy text-lg text-gray-600 max-w-2xl mx-auto" data-testid="how-it-works-description">
        {isHindi ? 'Artists और collaborators के लिए creative connection शुरू करने के तीन आसान steps.' : 'Three simple steps for artists and collaborators to start a creative connection.'}
              <span className="block mt-2 text-base text-gray-500">
                कलाकारों और सहयोगियों के लिए काम शुरू करने का आसान तरीका।
              </span>
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 items-stretch">
            <div
              className="relative flex h-full flex-col items-center rounded-3xl border border-orange-100 bg-white/85 px-6 py-9 text-center shadow-[0_18px_45px_rgba(15,23,42,0.08)] transition-transform duration-200 hover:-translate-y-1 hover:shadow-[0_22px_55px_rgba(15,23,42,0.12)]"
              data-testid="how-it-works-step-1"
            >
              <div className="w-20 h-20 bg-gradient-to-br from-[#FF6F00] to-[#FF8F00] rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg ring-8 ring-orange-50">
                <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-2">Post</h3>
              <p className="text-lg font-semibold text-[#FF6F00] mb-4">पोस्ट करें</p>
              <p className="mx-auto max-w-sm text-base leading-7 text-gray-700 mb-3">
                Artists can post their profile, photos, videos, and portfolio so the right people can discover their work.
              </p>
              <p className="mx-auto max-w-sm text-base leading-7 text-gray-600">
                कलाकार अपना प्रोफाइल, फोटो, वीडियो और पोर्टफोलियो पोस्ट करें ताकि सही लोग आपका काम देख सकें।
              </p>
            </div>
            <div
              className="relative flex h-full flex-col items-center rounded-3xl border border-purple-100 bg-white/85 px-6 py-9 text-center shadow-[0_18px_45px_rgba(15,23,42,0.08)] transition-transform duration-200 hover:-translate-y-1 hover:shadow-[0_22px_55px_rgba(15,23,42,0.12)]"
              data-testid="how-it-works-step-2"
            >
              <div className="w-20 h-20 bg-gradient-to-br from-[#4A148C] to-[#6A1B9A] rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg ring-8 ring-purple-50">
                <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-2">Connect</h3>
              <p className="text-lg font-semibold text-[#4A148C] mb-4">कनेक्ट करें</p>
              <p className="mx-auto max-w-sm text-base leading-7 text-gray-700 mb-3">
                Artists, organizers, production houses, and clients can connect directly and continue conversations through private messages.
              </p>
              <p className="mx-auto max-w-sm text-base leading-7 text-gray-600">
                कलाकार, आयोजक, प्रोडक्शन हाउस और क्लाइंट सीधे जुड़ें और निजी संदेशों से बात आगे बढ़ाएं।
              </p>
            </div>
            <div
              className="relative flex h-full flex-col items-center rounded-3xl border border-teal-100 bg-white/85 px-6 py-9 text-center shadow-[0_18px_45px_rgba(15,23,42,0.08)] transition-transform duration-200 hover:-translate-y-1 hover:shadow-[0_22px_55px_rgba(15,23,42,0.12)]"
              data-testid="how-it-works-step-3"
            >
              <div className="w-20 h-20 bg-gradient-to-br from-[#00BFA5] to-[#00D9B5] rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg ring-8 ring-teal-50">
                <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-2">Get Work</h3>
              <p className="text-lg font-semibold text-[#00BFA5] mb-4">काम पाएं</p>
              <p className="mx-auto max-w-sm text-base leading-7 text-gray-700 mb-3">
          Find the right creative opportunities, discuss details, and turn interest into projects.
              </p>
              <p className="mx-auto max-w-sm text-base leading-7 text-gray-600">
                सही अवसर पाएं, बातचीत करें और अपने हुनर के लिए नए काम और प्रोजेक्ट हासिल करें।
              </p>
            </div>
          </div>
        </div>
      </section>

      {SHOW_EXTENDED_HOME_SECTIONS && (
      <section className="section-padding bg-white" data-testid="partner-categories-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="launch-heading mx-auto max-w-3xl text-4xl sm:text-5xl font-bold gradient-text mb-4" data-testid="partner-categories-heading">
              Partner Categories
            </h2>
            <p className="launch-copy text-lg text-gray-600 max-w-2xl mx-auto" data-testid="partner-categories-description">
              Explore the kinds of organizations that can discover and connect with Kala Manch artists.
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {partners.map((partner, index) => (
              <Link
                key={partner.path}
                to={partner.path}
                className="bg-gray-50 rounded-xl p-8 flex flex-col items-center justify-center hover-lift transition-all duration-200 hover:bg-white hover:shadow-lg"
                data-testid={`partner-category-card-${index}`}
              >
                <div className="text-6xl mb-4" data-testid={`partner-category-icon-${index}`}>{partner.icon}</div>
                <h3 className="text-base font-semibold text-gray-700 text-center" data-testid={`partner-category-name-${index}`}>
                  {partner.name}
                </h3>
              </Link>
            ))}
          </div>
        </div>
      </section>
      )}

      {SHOW_EXTENDED_HOME_SECTIONS && (
      <section className="section-padding bg-gradient-to-br from-orange-50 via-white to-purple-50" data-testid="home-faq-preview">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-[0.8fr_1.2fr] gap-8 items-start">
            <div>
              <p className="text-sm font-bold uppercase tracking-[0.3em] text-[#FF6F00] mb-4">
                Quick answers
              </p>
              <h2 className="launch-heading max-w-xl text-4xl sm:text-5xl font-bold gradient-text mb-4">
                Questions before you join?
              </h2>
              <p className="launch-copy text-lg text-gray-600">
                A few launch-ready answers about signup, verification, privacy, and contacting artists or collaborators.
              </p>
              <Link to="/faq" data-testid="home-faq-view-all" onClick={() => trackEvent('home_faq_click')}>
                <button className="btn-secondary mt-7">
                  View All FAQs
                </button>
              </Link>
            </div>

            <div className="space-y-4">
              {faqPreviewItems.map((item) => (
                <details
                  key={item.question}
                  className="group rounded-3xl bg-white p-5 shadow-lg ring-1 ring-orange-100 transition hover:-translate-y-0.5 hover:shadow-xl"
                >
                  <summary className="cursor-pointer list-none text-lg font-bold text-gray-900">
                    <span className="inline-flex w-full items-center justify-between gap-4">
                      {item.question}
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-orange-50 text-[#FF6F00] transition group-open:rotate-45">
                        +
                      </span>
                    </span>
                  </summary>
                  <p className="mt-4 text-gray-600 leading-7">
                    {item.answer}
                  </p>
                </details>
              ))}
            </div>
          </div>
        </div>
      </section>
      )}

      {SHOW_EXTENDED_HOME_SECTIONS && (
      <section className="section-padding bg-white" data-testid="launch-trust-badges">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-[2.5rem] border border-orange-100 bg-gradient-to-br from-white via-orange-50/60 to-purple-50 p-6 sm:p-8 lg:p-10 shadow-xl">
            <div className="grid gap-8 lg:grid-cols-[0.75fr_1.25fr] lg:items-center">
              <div>
                <p className="text-sm font-bold uppercase tracking-[0.3em] text-[#00BFA5] mb-4">
                  Launch trust
                </p>
                <h2 className="launch-heading max-w-xl text-4xl sm:text-5xl font-bold gradient-text mb-4">
                  Built for safer creative work.
                </h2>
                <p className="launch-copy text-lg text-gray-600">
                  Kala Manch is designed so artists, collaborators, and visitors can discover talent with better
                  privacy, review, and support around every important action.
                </p>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                {launchTrustBadges.map((badge) => (
                  <div key={badge.title} className="rounded-3xl bg-white p-5 shadow-sm ring-1 ring-orange-100">
                    <div className={`mb-4 inline-flex rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wide ${badge.accent}`}>
                      Protected
                    </div>
                    <h3 className="text-lg font-bold text-gray-900">{badge.title}</h3>
                    <p className="mt-2 text-sm leading-6 text-gray-600">{badge.text}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
      )}

      {SHOW_MARKETING_HOME_SECTIONS && (
      <section className="gradient-bg section-padding text-white" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="launch-heading mx-auto max-w-3xl text-3xl sm:text-4xl lg:text-5xl font-bold mb-5" data-testid="cta-heading">
            {pageContent.cta_heading || 'Are You Ready?'}
            <span className="mt-2 block">Post...Connect...Get Work...</span>
          </h2>
          <p className="launch-copy mx-auto max-w-2xl text-lg sm:text-xl mb-8 text-gray-100" data-testid="cta-description">
            {pageContent.cta_description || 'Join as an artist or collaborator and start building real creative connections on Kala Manch.'}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              type="button"
              data-testid="cta-signup-button"
              onClick={() => {
                trackEvent('home_join_click', { location: 'final_cta' });
                window.dispatchEvent(new Event('openArtistJoin'));
              }}
              className="btn-primary bg-white text-[#FF6F00] hover:bg-gray-100"
            >
              Join Kala Manch
            </button>
            <Link to="/contact" data-testid="cta-learn-more-button" onClick={() => trackEvent('home_contact_click', { location: 'final_cta' })}>
              <button className="btn-outline border-white text-white hover:bg-white hover:text-[#4A148C]">
                Learn More
              </button>
            </Link>
          </div>
        </div>
      </section>
      )}
    </div>
  );
};

export default Home;










